import { useEffect, useState } from 'react'
import { mutate } from '@/runtime'
import { useAllLists, useAllPages, useAllProjects, useWorkspaces } from '@/db/hooks'
import { hashSecret, newSecret } from '@/kinds/grant'
import { Button, Field, Sheet } from '@/components/ui'
import { FromField } from '@/components/FromField'
import { SwitchRow } from '@/components/Switch'

export type ShareTarget = { kind: 'List' | 'Page' | 'Project'; id: string; name: string }
type What = 'capture' | 'capturepage' | 'list' | 'page' | 'project' | 'workspace'
type Life = 'forever' | 'week' | 'month' | 'once'

/**
 * Make a grant: a capture link (a page with a question, or a bare one for
 * someone's Shortcut), or a link to one list, page or project, or a suite.
 * Made from a person, it starts as a capture page for them. The secret is generated here, hashed, and the hash
 * is what syncs; the secret itself is shown once, with a QR code, then gone.
 */
export const ShareSheet = ({
  target,
  person,
  onClose,
}: {
  target?: ShareTarget
  /** a link for one person: what they send carries their handle, so it lands on their page */
  person?: { name: string; key: string }
  onClose: () => void
}) => {
  const lists = useAllLists() ?? []
  const pages = useAllPages() ?? []
  const projects = useAllProjects() ?? []
  const workspaces = useWorkspaces().filter((w) => !w.deleted_at && !w.status.archived_at)
  const [what, setWhat] = useState<What>(
    target ? (target.kind.toLowerCase() as What) : person ? 'capturepage' : 'capture',
  )
  const [targetId, setTargetId] = useState(target?.id ?? '')
  const [name, setName] = useState(
    target ? `${target.name} link` : person ? `${person.name}’s link` : 'Partner’s Shortcut',
  )
  const [from, setFrom] = useState(person?.key ?? 'partner')
  const [purpose, setPurpose] = useState(person ? 'Send me a thing to do' : '')
  const [context, setContext] = useState('')
  const [write, setWrite] = useState(true)
  const [life, setLife] = useState<Life>('forever')
  const [made, setMade] = useState<{ secret: string; url: string; header: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  const options =
    what === 'list'
      ? lists.filter((l) => !l.deleted_at).map((l) => ({ id: l.id, name: l.spec.title }))
      : what === 'page'
        ? pages.filter((p) => !p.deleted_at).map((p) => ({ id: p.id, name: p.name }))
        : what === 'project'
          ? projects.filter((p) => !p.deleted_at).map((p) => ({ id: p.id, name: p.spec.title }))
          : what === 'workspace'
            ? workspaces.map((w) => ({ id: w.spec.slug, name: w.spec.name }))
            : []

  const make = async () => {
    setError(null)
    try {
      if (what === 'capturepage' && !purpose.trim()) throw new Error('Say what you are asking for')
      const scopes =
        what === 'capture' || what === 'capturepage'
          ? ['capture']
          : what === 'workspace'
            ? targetId
              ? [`workspace:${targetId}:${write ? 'write' : 'read'}`]
              : []
            : targetId
              ? [`${what}:${targetId}:${what === 'list' && write ? 'write' : 'read'}`]
              : []
      if (!scopes.length) throw new Error('Pick what to share')
      const secret = newSecret()
      const token_hash = await hashSecret(secret)
      const expires_at =
        life === 'week'
          ? new Date(Date.now() + 7 * 86_400_000).toISOString()
          : life === 'month'
            ? new Date(Date.now() + 30 * 86_400_000).toISOString()
            : undefined
      await mutate.create(
        'Grant',
        {
          name: name.trim() || 'Share',
          scopes,
          token_hash,
          ...(expires_at ? { expires_at } : {}),
          ...(life === 'once' ? { max_uses: 1 } : {}),
          ...((what === 'capture' || what === 'capturepage' || what === 'workspace') && from.trim()
            ? { from: from.trim().toLowerCase() }
            : {}),
          ...(what === 'capturepage' && purpose.trim() ? { purpose: purpose.trim() } : {}),
          ...((what === 'capture' || what === 'capturepage') && context.trim()
            ? { context: context.trim() }
            : {}),
        },
        {},
      )
      setMade({
        secret,
        url:
          what === 'capture'
            ? `${origin}/api/capture`
            : what === 'workspace'
              ? origin
              : `${origin}/s/${secret}`,
        header: `Bearer ${secret}`,
      })
    } catch (e) {
      setError((e as Error).message)
    }
  }
  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(label)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied('Could not copy')
    }
  }

  return (
    <Sheet open onClose={onClose} title={made ? 'Ready to share' : 'Share'}>
      {made ? (
        <div data-testid="share-made">
          <p className="text-sm text-muted mb-3">
            This is shown once. Copy it now; you can revoke it any time in Settings → Sharing.
          </p>
          {what === 'capture' ? (
            <>
              <Field label="URL for the Shortcut">
                <div className="flex gap-2">
                  <input readOnly value={made.url} className="w-full font-mono text-sm" />
                  <Button onClick={() => void copy('URL', made.url)}>
                    {copied === 'URL' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </Field>
              <Field label="Authorization header">
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={made.header}
                    className="w-full font-mono text-sm"
                    data-testid="share-secret"
                  />
                  <Button onClick={() => void copy('Header', made.header)}>
                    {copied === 'Header' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </Field>
            </>
          ) : what === 'workspace' ? (
            <>
              <p className="text-sm text-muted mb-3">
                Send this to them privately. They open {origin} in a browser, add it to the home screen, and
                paste it on the first screen where it asks for a token. Anyone who has it can sign in as them,
                so treat it like a password.
              </p>
              <Field label="Invitation">
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={made.secret}
                    className="w-full font-mono text-sm"
                    data-testid="share-invitation"
                  />
                  <Button onClick={() => void copy('Invitation', made.secret)}>
                    {copied === 'Invitation' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </Field>
            </>
          ) : (
            <Field label="Link">
              <div className="flex gap-2">
                <input
                  readOnly
                  value={made.url}
                  className="w-full font-mono text-sm"
                  data-testid="share-url"
                />
                <Button onClick={() => void copy('Link', made.url)}>
                  {copied === 'Link' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </Field>
          )}
          <Qr text={what === 'capture' || what === 'workspace' ? made.secret : made.url} />
          {what === 'capturepage' && (
            <p className="text-xs text-faint">
              {life === 'once'
                ? 'One send, then the link is gone.'
                : 'They can send as often as the link lasts.'}
            </p>
          )}
          <div className="flex justify-end mt-3">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void make()
          }}
          data-testid="share-form"
        >
          {!target && (
            <Field label="What">
              <select
                value={what}
                onChange={(e) => {
                  setWhat(e.target.value as What)
                  setTargetId('')
                  // a login has no use to burn
                  if (e.target.value === 'workspace' && life === 'once') setLife('forever')
                }}
                className="w-full"
                data-testid="share-what"
              >
                <option value="capturepage">A capture link: a page with a question on it</option>
                <option value="capture">A capture link for someone’s Shortcut</option>
                <option value="list">A list</option>
                <option value="page">A page (read-only)</option>
                <option value="project">A project (read-only)</option>
                <option value="workspace">A whole suite: they sign in to the app</option>
              </select>
            </Field>
          )}
          {what !== 'capture' && what !== 'capturepage' && !target && (
            <Field
              label={
                what === 'list'
                  ? 'Which list'
                  : what === 'page'
                    ? 'Which page'
                    : what === 'workspace'
                      ? 'Which suite'
                      : 'Which project'
              }
              hint={
                what === 'workspace'
                  ? 'They see and change only what is in this suite: its tasks, projects, pages and lists. Nothing else of yours is sent to them.'
                  : undefined
              }
            >
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="w-full"
                data-testid="share-target"
              >
                <option value="">Choose…</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Name" hint="For you: who or what this is for.">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full"
              data-testid="share-name"
            />
          </Field>
          {what === 'capturepage' && (
            <Field
              label="What you are asking them"
              hint="This is the page they see, e.g. “What should I pack for Lisbon?”"
            >
              <input
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                className="w-full"
                data-testid="share-purpose"
              />
            </Field>
          )}
          {(what === 'capture' || what === 'capturepage') && (
            <Field
              label="Private context for the assistant"
              hint="They never see this. It rides along with everything they send, e.g. “this is for the October trip; file under the Lisbon project”."
            >
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                rows={2}
                className="w-full"
                data-testid="share-context"
              />
            </Field>
          )}
          {(what === 'capture' || what === 'capturepage' || what === 'workspace') && (
            <FromField
              value={from}
              onChange={setFrom}
              label={what === 'workspace' ? 'Who' : 'From'}
              hint={
                what === 'workspace'
                  ? 'Links this login to their record in People, so “Share with…” can reach their app.'
                  : undefined
              }
              testId="share-from"
            />
          )}
          {(what === 'list' || what === 'workspace') && (
            <SwitchRow
              className="mb-3"
              label={what === 'workspace' ? 'They can change things' : 'They can tick and add items'}
              hint={what === 'workspace' ? 'Off, they only look.' : undefined}
              on={write}
              onChange={setWrite}
              testId="share-write"
            />
          )}
          <Field label="Lasts">
            <select
              value={life}
              onChange={(e) => setLife(e.target.value as Life)}
              className="w-full"
              data-testid="share-life"
            >
              <option value="forever">Until revoked</option>
              <option value="week">A week</option>
              <option value="month">A month</option>
              {what !== 'workspace' && (
                <option value="once">
                  {what === 'capturepage' ? 'One send, then gone' : 'One open, then gone'}
                </option>
              )}
            </select>
          </Field>
          {error && <p className="text-sm text-rose-300 mb-3">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" data-testid="share-make">
              Make the link
            </Button>
          </div>
        </form>
      )}
    </Sheet>
  )
}

/** A QR of the link or secret, rendered on the device (nothing leaves it). */
const Qr = ({ text }: { text: string }) => {
  const [src, setSrc] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    void import('qrcode').then(async (q) => {
      const url = await q.toDataURL(text, {
        margin: 1,
        width: 220,
        color: { dark: '#000000', light: '#ffffff' },
      })
      if (!cancelled) setSrc(url)
    })
    return () => {
      cancelled = true
    }
  }, [text])
  if (!src) return null
  return (
    <div className="flex justify-center my-3">
      <img
        src={src}
        alt="QR code of the share"
        width={220}
        height={220}
        className="rounded-xl bg-white p-2"
      />
    </div>
  )
}
